import { useState, useCallback } from 'react';

// Extend Window interface to include google
declare global {
  interface Window {
    google?: {
      maps?: {
        places?: unknown;
      };
    };
  }
}

interface GoogleMapsLoaderState {
  isLoaded: boolean;
  loadError: Error | null;
}

// Singleton state to prevent multiple loads
let isLoaded = false;
let loadError: Error | null = null;
let loadPromise: Promise<void> | null = null;

/**
 * Load Google Maps API by adding script tag to document
 */
const loadGoogleMapsScript = (apiKey: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google?.maps?.places) {
      resolve();
      return;
    }

    // Check if script is already in DOM
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Wait for it to load
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps')));
      return;
    }

    // Create and append script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps API'));

    document.head.appendChild(script);
  });
};

/**
 * Hook to load the Google Maps JavaScript API with Places library
 * Uses a singleton pattern to ensure the API is only loaded once
 */
export const useGoogleMapsLoader = (): GoogleMapsLoaderState & { loadGoogleMaps: () => Promise<void> } => {
  const [state, setState] = useState<GoogleMapsLoaderState>({
    isLoaded,
    loadError,
  });

  const loadGoogleMaps = useCallback(async () => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey) {
      const error = new Error('Google Maps API key not configured');
      loadError = error;
      setState({ isLoaded: false, loadError: error });
      return;
    }

    // If already loaded, return immediately
    if (isLoaded) {
      setState({ isLoaded: true, loadError: null });
      return;
    }

    // If loading is in progress, wait for it
    if (loadPromise) {
      try {
        await loadPromise;
        setState({ isLoaded: true, loadError: null });
      } catch (error) {
        setState({ isLoaded: false, loadError: error as Error });
      }
      return;
    }

    // Start loading
    loadPromise = loadGoogleMapsScript(apiKey);

    try {
      await loadPromise;
      isLoaded = true;
      loadError = null;
      setState({ isLoaded: true, loadError: null });
    } catch (error) {
      loadError = error as Error;
      setState({ isLoaded: false, loadError: error as Error });
    }
  }, []);

  // NOTE: We intentionally do NOT auto-load on mount.
  // Google Maps SDK triggers a "local network access" permission dialog in browsers.
  // By lazy-loading only when explicitly requested, we delay this dialog until
  // the user actually needs address autocomplete functionality.
  // Components should call loadGoogleMaps() when they need the API.

  return {
    ...state,
    loadGoogleMaps,
  };
};

export default useGoogleMapsLoader;
